import * as React from 'react';
import { EserviceWrapper } from './EserviceWrapper';
import { CheckBoxGroupState } from 'sos-common/js/components/FormControls/CheckBoxGroup';
import { SelectBoxOption } from 'sos-common/js/components/FormControls/SelectBox';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { makeTranslator } from 'sos-common/js/utils/translations';
import { IAppContext, AppContext } from './Types/Intyg.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { ResourceItem } from 'sos-common/js/commonTypes/resourceItem';
import { dialogueErrorBody } from 'sos-eservice-common/js/config/dialogueErrorBodies';
import { IntygsAnsokan, IKeyValue, IFolkbokforingsAdress } from './Types/ansokan.types';
import { ApplicationType, FormType } from './Types/Intyg.enums';
import _ from 'lodash';
import { ApiService } from './Services/intyg.api.service';
import { ReCaptchaWrapper } from './ReCaptchaWrapper';
import { DialogueError } from 'sos-common/js/components/DialogueError/DialogueError';
import { getDialogueByResponse as eServiceDialogueHelper } from '../../helpers/dialogue.helper';
import { getDialogueByResponse as webFormDialogueHelper } from '../../helpers/webform.dialogue.helper';

export interface IntygApplicationProps {
  intygsAnsokan: IntygsAnsokan;
  applicationType: ApplicationType;
  formType: FormType;
  translation: ResourceItem[];
  editableAreas: IKeyValue[];
  heading?: string;
  subheading?: string;
  isInEditMode: boolean;
  contentId?: number;
  yrken: SelectBoxOption[];
  thankYouPage?: string;
  folkbokforing?: IFolkbokforingsAdress;
  stepTwoPageLink?: string;
  clientKey?: string;
  maxTotalSizeInBytes: number;
  myPageLink?: string;
  yrkeSpecificInformationBoxes: [[string, string]];
  maxIndividualFileSizeInBytes?: number;
  formLanguage: string;
  acceptedFileTypes: string[];
}

export interface IntygApplicationState {
  appContext: IAppContext;
  isErrorModalOpen?: boolean;
}

export class IntygApplication extends React.Component<IntygApplicationProps, IntygApplicationState> {
  private _latestResponseError: ResponseError;
  constructor(props: IntygApplicationProps) {
    super(props);

    // Initialize singleton with onApiError callback
    ApiService.getInstance().setErrorCallback(this.onApiError);

    const {
      applicationType,
      formType,
      editableAreas,
      translation,
      intygsAnsokan,
      isInEditMode,
      yrken,
      thankYouPage,
      folkbokforing,
      stepTwoPageLink,
      maxTotalSizeInBytes,
      maxIndividualFileSizeInBytes,
      myPageLink,
      yrkeSpecificInformationBoxes,
      formLanguage,
      acceptedFileTypes,
    } = props;

    const appContext: IAppContext = {
      Translate: makeTranslator(translation),
      updateModelUsingKeyValue: this.updateModelUsingKeyValue,
      updateModelAsBatch: this.updateModelAsBatch,
      onChangeFieldValue: this.onChangeFieldValue,
      onChangeCheckboxGroupValue: this.onChangeCheckboxGroupValue,
      forceComponentTreeReRender: this.forceComponentTreeReRender,
      onClickSubmitButton: () => {
        console.log('override me');
      },
      applicationType,
      formType,
      model: this.getMergedModel(props.intygsAnsokan),
      isInEditMode,
      ansokanId: intygsAnsokan?.id,
      yrken,
      editableAreas,
      thankYouPage,
      folkbokforing,
      stepTwoPageLink,
      clientKey: props.clientKey,
      myPageLink,
      bekraftaepost: props.intygsAnsokan.epost,
      contentId: props.contentId,
      yrkeSpecificInformationBoxes: new Map(yrkeSpecificInformationBoxes),
      showSpinner: false,
      maxIndividualFileSizeInBytes,
      maxTotalSizeInBytes,
      formLanguage,
      acceptedFileTypes,
      setSubmitButtonAsClicked: (callback = () => {}) => {
        const appContext = _.cloneDeep(this.state.appContext);

        appContext.submitButtonIsClicked = true;
        this.setState({ appContext }, callback);
      },
      submitButtonIsClicked: false,
    };

    this.state = {
      appContext,
      isErrorModalOpen: false,
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as IntygApplicationProps;

  //webbform has extra fields in personuppgifter section
  getMergedModel = (intygsAnsokan: IntygsAnsokan): IntygsAnsokan => {
    if (this.props.applicationType === ApplicationType.Eservice) return intygsAnsokan;
    return {
      ...intygsAnsokan,
      personnummer: null,
      fornamn: null,
      efternamn: null,
      gatuadress: null,
      postnummer: null,
      postort: null,
      bekraftaepost: null,
    };
  };

  //There is a control for the existing value. Otherwise the update method is called repeatedly until the user closes the dialogue.
  onApiError = (responseError: ResponseError) => {
    if (this.state.isErrorModalOpen === false) {
      this._latestResponseError = responseError;
      const appContext = _.cloneDeep(this.state.appContext);
      appContext.showSpinner = false;
      this.setState({ isErrorModalOpen: true, appContext });
    }
  };

  onChangeCheckboxGroupValue = (checkboxGroup: CheckBoxGroupState) => {
    const appContext = _.cloneDeep(this.state.appContext);
    checkboxGroup.childrenFieldNames.forEach((fieldName) => {
      const isChecked = checkboxGroup.value.find((x) => x === fieldName) !== undefined;
      this.setModelField(appContext, fieldName, isChecked);
    });
    this.setState({ appContext });
  };

  onChangeFieldValue = (field: FormField<any>, callback = () => {}) => {
    let appContext = _.cloneDeep(this.state.appContext);
    appContext = this.setModelField(appContext, field.fieldName, field.value);
    appContext.isFormTouched = true;
    this.setState({ appContext }, callback);
  };

  updateModelUsingKeyValue = (key: string, value: any, callback = () => {}) => {
    let appContext = _.cloneDeep(this.state.appContext);
    appContext = this.setModelField(appContext, key, value);
    appContext.isFormTouched = true;
    this.setState({ appContext }, callback);
  };

  updateModelAsBatch = (arr: IKeyValue[], callback = () => {}) => {
    let appContext = _.cloneDeep(this.state.appContext);
    arr.forEach((kvp) => {
      appContext = this.setModelField(appContext, kvp.key, kvp.value);
    });
    appContext.isFormTouched = true;
    this.setState({ appContext }, callback);
  };

  forceComponentTreeReRender = () => {
    let appContext = _.cloneDeep(this.state.appContext);
    this.setState({ appContext });
  };

  setModelField = (appContext: IAppContext, modelName: string, value: any) => {
    const modelFieldExists = appContext.model[modelName] !== undefined;
    if (!modelFieldExists) console.error('No such fieldname in model.');
    if (modelFieldExists) {
      appContext.model[modelName] = value;
    }
    return appContext;
  };

  onModalClose = () => {
    this.setState({ isErrorModalOpen: false });
  };

  getDialogueBody = (): dialogueErrorBody => {
    const { appContext } = this.state;

    if (appContext.applicationType === ApplicationType.Eservice) {
      return eServiceDialogueHelper(this._latestResponseError, appContext.myPageLink);
    }

    if (appContext.applicationType === ApplicationType.Webform) {
      return webFormDialogueHelper(this._latestResponseError.body.errorMessage);
    }
  };

  public render() {
    const { applicationType } = this.props;
    const { appContext, isErrorModalOpen } = this.state;

    return (
      <AppContext.Provider value={appContext}>
        {isErrorModalOpen && (
          <DialogueError
            dialogueErrorBody={this.getDialogueBody()}
            isModalOpen={true}
            onModalClose={this.onModalClose}
            enableCloseIcon={false}
            shouldCloseOnOverlayClick={false}
          />
        )}
        {applicationType === ApplicationType.Eservice && <EserviceWrapper />}
        {applicationType === ApplicationType.Webform && <ReCaptchaWrapper />}
      </AppContext.Provider>
    );
  }
}
